import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { isEmpty, takeOnce } from '@partners/utils';
import { map, tap } from 'rxjs';
import { IFlatStage, INavigationOptions, IStage, IStageQueryCriteria, IStageState } from '../models';

export const DEFAULT_NAVIGATION_OPTIONS: INavigationOptions = {
  isExplicitNavigation: false,
  navOutsideZone: false
};

/**
 * @description
 * A stateless navigation service. it enables strict (limited) navigation privileges in the app.
 * the service relies on an array of stage data nodes which dictates the navigation restrictions.
 * when a navigation event triggers, the service searches the target route's identifier (id|path)
 * in the given stages collection, if a match is found - permission to navigate is given according
 * to a linear navigation restriction, meaning: navigation to target is allowed only if the target
 * is specified as a target stage of the current stage.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  /**
   * @description Gets the flat stages collection and routeId, checks if the requested stage navigation is allowed,
   * and navigates to the the next stage feature.
   * Emits error if the stage wasn't found, or if it is not allowed to navigate to the requested stage.
   * @param flatStages The flat stages collection.
   * @param currentStageId The route id of the requested stage.
   */
  moveNext(currentStageId: number, flatStages: IFlatStage[], options?: INavigationOptions) {
    options = { ...DEFAULT_NAVIGATION_OPTIONS, ...options };

    this.activatedRoute.url,
      this.activatedRoute.params
        .pipe(
          takeOnce(),
          map((params) => this.filterParamsFromUrl(this.router.url, params)),
          tap((url) => {
            const currentStage = this.findStageByFullUrl(flatStages, url);

            if (!currentStage) {
              this.throwStageNotFoundError();
            }

            /** route is reloaded (possibly browser refresh) */
            if (currentStage.nextPossibleStage === currentStageId) {
              return;
            }

            let nextStage = this.findStageById(flatStages, currentStage.nextPossibleStage);

            if (!nextStage) {
              /**
               * when navigating implicitly via "moveNext" we assume there is a matching stage for
               * current & next route. otherwise implicit navigation cant be completed and so - we
               * throw an error
               **/
              this.throwStageNotFoundError();
            }

            /** when navigating explicitly we ignore the isCompleted state of the stage */
            if (options.isExplicitNavigation) {
              nextStage = { ...nextStage, ...{ isComplete: false } };
            }

            if (nextStage.isComplete) {
              /**
               * restricted navigation disallow to navigate to a stage marked as completed, meaning -
               * the user cant navigate back
               */
              this.throwNavigationNotAllowedError();
            }

            this.router.navigateByUrl(nextStage.url);
          })
        )
        .subscribe();
  }

  /**
   * in skip stages navigation mode (where we are navigating actual sale screens without
   * stages map) - we still need to manage a strict navigation (currently - this means
   * preventing backward navigation). since there are no stage or flatStages maps - we
   * keep track of the navigation routes by storing their urls in anonymousStages array
   * in the shell store. on each NavigationEnd we store the anonymous stage in the array,
   * navigating backward will fail if target url exist inside anonymousStages array
   * @param targetUrl the url we want to navigate to
   * @param anonymousStages a collection of stages url (when there is no stages map)
   * @param params any params exist on the route
   * @returns boolean indication: true - navigation is allowed. false - url already visited -
   * navigation nis denied
   */
  canLoadAnonymousRoute(targetUrl: string, anonymousStages: Partial<IStage>[], params?: Params): boolean {
    targetUrl = this.filterParamsFromUrl(targetUrl, params);

    const anonymous = anonymousStages.find(({ url }) => url === targetUrl);

    return isEmpty(anonymous);
  }

  canLoadRoute(targetUrl: string, flatStages: IFlatStage[], params?: Params): boolean {
    targetUrl = this.filterParamsFromUrl(targetUrl, params);

    const targetStage: IFlatStage = this.findStageByFullUrl(flatStages, targetUrl);

    if (isEmpty(targetStage)) {
      /** current route has no matching stage, therefore we don not restrict navigating to it */
      return true;
    }

    if (targetStage.isComplete) {
      return false;
    }

    const isFirstStage = !!!targetStage.preMandatoryStage;

    if (isFirstStage) {
      return true;
    }

    // const previousStage: IFlatStage = this.findStageById(flatStages, targetStage.preMandatoryStage);

    // if (isEmpty(previousStage)) {
    //   /** target route has no matching stage, therefore we cant restrict navigating to it */
    //   return true;
    // }

    /** both source and target are part of the stages map - restrict navigation to map rules */
    return !targetStage.isComplete;
  }

  /**
   * @description Receives a flat stages collection and routeId, checks if the requested stage navigation is exist,
   * and navigates to the the next stage feature.
   * Emits error if the stage wasn't found.
   * @param flatStages The flat stages collection.
   * @param stageId The route id of the requested stage.
   */
  moveTo(flatStages: IFlatStage[], stageId: number, options?: any) {
    const nextStage = this.findStageById(flatStages, stageId);
    if (!nextStage) {
      this.throwStageNotFoundError();
    }

    this.router.navigateByUrl(nextStage.name);
  }

  moveAny(url: string, options?: INavigationOptions) {
    this.router.navigateByUrl(url, options);
  }

  /**
   * @description Receives a nested (non flattened) stages input, and maps the stages to flat stages hierarchy.
   * @param sourceStages A nested stages hierarchy array.
   * @returns A flat stages hierarchy array.
   */
  syncRouter(sourceStages: IStage[]) {
    const flatStages: IFlatStage[] = [];

    for (const stage of sourceStages) {
      this.mapStage(sourceStages, flatStages, stage, [stage.name]);
    }

    return flatStages;
  }

  /**
   * @description Receives a flat stages hierarchy input, and updates the stages status.
   * @param flatStages A flat stages hierarchy array.
   * @returns An status updated flat stages hierarchy array.
   */
  updateStagesState(flatStages: IFlatStage[]) {
    /**
     * update current stage (current stage becomes "complete" stage):
     * take the complete flat stages array, a search query object and a state object, then -
     * find the target stage using the search query and update the stage's state flags
     */
    this.editStageNode(
      flatStages,
      { isCurrent: true },
      {
        isCurrent: false,
        isPristine: false,
        isComplete: true
      }
    );

    /**
     * * update next stage (next stage becomes "current" stage):
     * take the complete flat stages array, a search query object and a state object, then -
     * find the target stage using the search query and update the stage's state flags
     */
    this.editStageNode(
      flatStages,
      { path: this.router.url },
      {
        isCurrent: true,
        isPristine: false,
        isComplete: false
      }
    );

    return flatStages;
  }

  /**
   * @description Receives a flat stages array, and finds the stage by comparing its path to current url.
   * @param flatStages Flat stages array.
   * @returns Current stage.
   */
  findStageByCurrentUrl(flatStages: IFlatStage[]): IFlatStage {
    return this.findStageByFullUrl(flatStages, this.router.url);
  }

  findStageIndex(flatStages: IFlatStage[], searchCriteria: Partial<IStageQueryCriteria>): number {
    const { path, id, code, isCurrent: isActive } = searchCriteria;

    const searchPredicate = (flatStage: IFlatStage) => {
      if (isActive) {
        return flatStage.isCurrent === isActive;
      }
      if (path) {
        return flatStage.url === path;
      }
      if (id) {
        return flatStage.screenCode === id;
      }
      if (code) {
        return flatStage.stageCode === code;
      }
      return false;
    };

    return flatStages.findIndex(searchPredicate);
  }

  /**
   * @description Receives a flat stages hierarchy input, and find the stage by route id.
   * @param flatStages A flat stages hierarchy array.
   * @param stageId Stage route id.
   * @returns Requested stage.
   */
  findStageById(flatStages: IStage[], stageId: number) {
    return flatStages.find((flatStage) => flatStage.screenCode === stageId);
  }

  editStageNode(
    flatStages: IFlatStage[],
    searchCriteria: IStageQueryCriteria,
    updateOptions: Partial<IStageState> = {}
  ): IFlatStage[] {
    const currentIndex = this.findStageIndex(flatStages, searchCriteria);
    /**
     * target index not found - we assume the target route does not require
     * authorization or other privileges to load (such are the message pages)
     */
    if (!~currentIndex) {
      return flatStages;
    }

    /**
     * is target route has corresponding stage the it needs to follow the
     * strict rule of linear navigation
     */
    const currentStage = flatStages[currentIndex];

    const updatedStage = this.updateStageState(currentStage, updateOptions);

    flatStages[currentIndex] = { ...updatedStage };

    return flatStages;
  }

  private updateStageState(targetNode: IStage | IFlatStage, updateOptions: Partial<IStageState> = {}): IFlatStage {
    targetNode = Object.assign({ ...targetNode }, { ...updateOptions });
    return targetNode as IFlatStage;
  }

  /**
   * @description Receives a flat stages hierarchy input, and find the stage by path.
   * @param flatStages A flat stages hierarchy array.
   * @param url The path to search the stage by.
   * @returns Current stage.
   */
  private findStageByFullUrl(flatStages: IFlatStage[], url: string): IFlatStage {
    return flatStages.find((flatStage) => url.startsWith(flatStage.url));
  }

  /**
   * @description A recursive function which fills a flat stage hierarchy, due to the stages logic.
   * @param flatStages An array which the function fills flat stage hierarchy into.
   * @param stage The current stage in the recursive process.
   * @param pathArray An array which includes the hierarchical names of the stage parents.
   * @param parentStage The parent stage in a recursive case, Optional (received when the stage isa child of another stage).
   */
  private mapStage(
    sourceStages: IStage[],
    flatStages: IFlatStage[],
    stage: IStage,
    pathArray: string[],
    parentStage?: IStage
  ) {
    if (!stage.children) {
      // Create a full string path, which contains all accentors' names
      const url = this.concatPathFromArray(pathArray);

      /**
       * In case the stage is a child, and it has No nextPossibleStage (=0), then the nextPossibleStage
       * will be the parent's nextPossibleStage.
       */
      const isLastChild = parentStage && !stage.nextPossibleStage;
      let nextPossibleStage = isLastChild ? parentStage.nextPossibleStage : stage.nextPossibleStage;

      const nextStageParent = this.findSourceStage(nextPossibleStage, sourceStages);
      if (nextStageParent?.children?.length) {
        nextPossibleStage = nextStageParent.children[0].screenCode;
      }

      /**
       * In case the stage is a child, and it has NO preMandatoryStage (=0), then the preMandatoryStage
       * will be the parent's preMandatoryStage.
       */
      const isFirstChild = parentStage && !stage.preMandatoryStage;
      let preMandatoryStage = isFirstChild ? parentStage.preMandatoryStage : stage.preMandatoryStage;

      const nextFlatStage = this.updateStageState(
        {
          ...stage,
          url,
          nextPossibleStage,
          preMandatoryStage
        },
        { isPristine: true, isCurrent: false, isComplete: false }
      );

      flatStages.push(nextFlatStage);
    } else {
      stage.children.forEach((child) =>
        this.mapStage(sourceStages, flatStages, child, [...pathArray, child.name], stage)
      );
    }
  }

  private findSourceStage(screenCode: number, stages: IStage[]): IStage | undefined {
    let found;
    for (let i = 0; i < stages.length; i++) {
      if (stages[i].children?.length) {
        let found = this.findSourceStage(screenCode, stages[i].children);
      }
      if (stages[i].screenCode === screenCode) {
        let found = stages[i];
        return found;
      }
    }
    return found;
  }

  /**
   * @description Create a path from array of strings.
   * @param pathArray An array of string.
   * @returns A result path.
   */
  private concatPathFromArray(pathArray: string[]) {
    return pathArray.reduce((pathString, name) => pathString.concat('/' + name), '');
  }

  private throwStageNotFoundError() {
    throw new Error('Stage not found');
  }

  private throwNavigationNotAllowedError() {
    throw new Error('Cannot navigate to requested page');
  }

  /**
   * @description Filters the params substrings from the url.
   * @param url The url to filter.
   * @param params The url params object.
   * @returns A filtered url string with no params.
   */
  private filterParamsFromUrl(url: string, params: Params) {
    // Used to remove query params.
    url = url.split('?')[0];

    url = Object.keys(params).reduce((url, key) => {
      return url.replace('/' + params[key], '');
    }, url);

    return url;
  }
}
